/**
 * Copiar mensagem — texto OU a propria imagem (#4050).
 *
 * Antes ia sempre `writeText(message.body)`. Em mensagem de midia o `body` e o
 * NOME do arquivo, entao "copiar" colava "teste.png" no destino em vez da
 * imagem. Para colar a imagem em outro programa e preciso por o ARQUIVO na
 * area de transferencia (`clipboard.write` + `ClipboardItem`), nao texto.
 *
 * Tres armadilhas que moldaram este codigo:
 *
 * 1. **So PNG cola em todo lugar.** Chrome aceita apenas `image/png` no
 *    ClipboardItem — um JPEG vai direto para o erro. Por isso convertemos o
 *    que nao for PNG antes de copiar.
 * 2. **Safari perde o gesto do usuario.** Se a imagem for baixada e SO depois
 *    o `write` acontecer, o clique ja "expirou" e o navegador recusa. Por isso
 *    passamos a PROMESSA do blob ao ClipboardItem e chamamos `write` na hora,
 *    ainda dentro do clique.
 * 3. **Nem todo navegador tem `clipboard.write`.** Firefox antigo e contexto
 *    sem HTTPS so tem `writeText`. Ai copiamos o texto, como antes — melhor
 *    que falhar.
 *
 * Devolve o que foi copiado ("selecao" | "imagem" | "texto") para quem chama
 * escolher o aviso.
 */

/** O que o ClipboardItem aceita em todos os navegadores. */
const TIPO_SUPORTADO = "image/png";

/** A mensagem carrega uma imagem que da para copiar? */
export function ehImagemCopiavel(message) {
  return !!(
    message &&
    message.mediaUrl &&
    message.mediaType === "image" &&
    !message.isDeleted
  );
}

/**
 * O navegador sabe por ARQUIVO na area de transferencia?
 *
 * `Item` entra por parametro (e nao so pelo global) porque quem chama ja
 * resolveu qual ClipboardItem usar — e porque o global nao existe fora do
 * navegador, o que tornaria a funcao impossivel de testar.
 */
export function suportaCopiaDeImagem(
  nav = typeof navigator !== "undefined" ? navigator : null,
  Item = typeof ClipboardItem !== "undefined" ? ClipboardItem : null
) {
  return !!(nav && nav.clipboard && typeof nav.clipboard.write === "function" && Item);
}

/**
 * Converte para PNG quando preciso. JPEG/WebP nao sao aceitos pelo Chrome na
 * area de transferencia, e o erro so aparece no `write` — tarde demais.
 */
async function paraPng(blob, deps = {}) {
  if (blob.type === TIPO_SUPORTADO) return blob;

  const criarBitmap = deps.createImageBitmap || window.createImageBitmap;
  const bitmap = await criarBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      png => (png ? resolve(png) : reject(new Error("falha ao converter para PNG"))),
      TIPO_SUPORTADO
    );
  });
}

/** Baixa a midia e devolve ja em PNG. */
async function baixarComoPng(url, deps) {
  const buscar = deps.fetch || window.fetch;
  const resposta = await buscar(url);
  if (!resposta.ok) throw new Error(`falha ao baixar a imagem (${resposta.status})`);
  return paraPng(await resposta.blob(), deps);
}

/**
 * Copia a mensagem. `deps` existe para o teste — em producao usa o navegador.
 */
export async function copiarMensagem(message, deps = {}) {
  const nav = deps.navigator || (typeof navigator !== "undefined" ? navigator : null);
  const Item = deps.ClipboardItem || (typeof ClipboardItem !== "undefined" ? ClipboardItem : null);
  const texto = () => (message && message.body) || "";

  // Selecao do usuario ganha de tudo (#4055): ele marcou o trecho que quer.
  const selecionado = (deps.selectedText || "").trim();
  if (selecionado) {
    await nav.clipboard.writeText(selecionado);
    return "selecao";
  }

  if (ehImagemCopiavel(message) && suportaCopiaDeImagem(nav, Item)) {
    try {
      // A promessa vai INTEIRA para o ClipboardItem: o `write` acontece agora,
      // ainda sob o clique, e o download resolve depois (ver armadilha 2).
      await nav.clipboard.write([
        new Item({ [TIPO_SUPORTADO]: baixarComoPng(message.mediaUrl, deps) })
      ]);
      return "imagem";
    } catch (err) {
      // Imagem fora de alcance (CORS, formato, permissao): copiar o texto
      // ainda e util e e o que o sistema fazia antes.
      if (deps.onError) deps.onError(err);
    }
  }

  await nav.clipboard.writeText(texto());
  return "texto";
}

export default copiarMensagem;
